// src/components/common/RichTextEditor.js
//
// Editor WYSIWYG basado en Quill (react-quill-new).
//
// Soporta:
//   - Formato de texto: H1/H2/H3, negrita, cursiva, subrayado, tachado,
//     color, resaltado, listas, indentación, alineación.
//   - Bloque: cita, código, enlace.
//   - Multimedia:
//       * Imagen: handler custom. Al hacer click abre un file picker,
//         sube el archivo al backend (necesita contenidoId) y el blot
//         de Quill inserta la URL firmada en el HTML.
//       * Video: usa el blot nativo de Quill. Al hacer click pide una
//         URL (YouTube, Vimeo, cualquier mp4 público) y la inserta
//         como iframe. Para subir un video propio al bucket usar
//         un contenido tipo Video (no en el editor de Material).
//
// Props:
//   value, onChange            — HTML del editor
//   contenidoId                — Guid del contenido al que pertenece
//                                 el material. REQUERIDO para habilitar
//                                 el botón de imagen. Si no se pasa, el
//                                 botón queda disponible pero avisa que
//                                 primero hay que crear el contenido.
//   label, helperText, placeholder, minHeight

import React, { useMemo, useRef, useCallback, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'quill/dist/quill.snow.css';
import {
  Box, FormHelperText, InputLabel, useTheme,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, Alert,
  CircularProgress,
} from '@mui/material';
import { certificadoService } from '../../services/api';

export default function RichTextEditor({
  value, onChange, label, helperText,
  minHeight = 240, placeholder = 'Escribe aquí...',
  contenidoId,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const quillRef = useRef(null);
  const [subiendo, setSubiendo] = useState(false);
  const [errorUpload, setErrorUpload] = useState('');
  const [dialogVideo, setDialogVideo] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  // ─── Handler custom del botón "imagen" ──────────────────────────────
  // Quill por defecto pide una URL. Lo reemplazamos para que abra un
  // file picker, suba el archivo al backend y luego inserte la URL
  // firmada en la posición del cursor.
  const handleImage = useCallback(() => {
    if (!contenidoId) {
      alert('Primero guarda el material para habilitar la subida de imágenes.');
      return;
    }
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/png,image/jpeg,image/webp,image/gif,image/svg+xml');
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      setSubiendo(true);
      setErrorUpload('');
      try {
        const { signedUrl } = await certificadoService.subirImagenMaterial(contenidoId, file);
        const quill = quillRef.current?.getEditor?.();
        if (!quill || !signedUrl) return;
        const range = quill.getSelection(true);
        quill.insertEmbed(range.index, 'image', signedUrl, 'user');
        quill.setSelection(range.index + 1);
      } catch (err) {
        setErrorUpload(err.message || 'Error subiendo la imagen.');
      } finally {
        setSubiendo(false);
      }
    };
    input.click();
  }, [contenidoId]);

  // ─── Handler custom del botón "video" ───────────────────────────────
  // Abre un diálogo donde el admin pega la URL de YouTube/Vimeo/mp4.
  // El blot nativo de Quill convierte automáticamente URLs de
  // YouTube/Vimeo al iframe de embed correcto.
  const handleVideo = useCallback(() => {
    setVideoUrl('');
    setDialogVideo(true);
  }, []);

  const insertarVideo = () => {
    const url = videoUrl.trim();
    if (!url) return;
    const quill = quillRef.current?.getEditor?.();
    if (!quill) return;
    const range = quill.getSelection(true);
    quill.insertEmbed(range.index, 'video', url, 'user');
    quill.setSelection(range.index + 1);
    setDialogVideo(false);
  };

  // ─── Config del toolbar + handlers ──────────────────────────────────
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ header: [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ align: [] }],
        ['blockquote', 'code-block'],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: { image: handleImage, video: handleVideo },
    },
    clipboard: { matchVisual: false },
  }), [handleImage, handleVideo]);

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'list', 'bullet', 'indent',
    'align',
    'blockquote', 'code-block',
    'link', 'image', 'video',
  ];

  return (
    <Box>
      {label && (
        <InputLabel sx={{ mb: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}>
          {label}
        </InputLabel>
      )}
      <Box
        sx={{
          position: 'relative',
          '& .ql-toolbar': {
            borderColor: 'divider',
            borderTopLeftRadius: 1, borderTopRightRadius: 1,
            bgcolor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            '& .ql-stroke': { stroke: isDark ? '#e0e0e0' : '#424242' },
            '& .ql-fill':   { fill:   isDark ? '#e0e0e0' : '#424242' },
            '& .ql-picker-label':   { color: isDark ? '#e0e0e0' : '#424242' },
            '& .ql-picker-options': { bgcolor: isDark ? '#1e1e2e' : '#ffffff' },
          },
          '& .ql-container': {
            borderColor: 'divider',
            borderBottomLeftRadius: 1, borderBottomRightRadius: 1,
            fontFamily: 'inherit', fontSize: '0.95rem',
            minHeight,
          },
          '& .ql-editor': {
            minHeight,
            color: 'text.primary',
            '&.ql-blank::before': { color: 'text.disabled', fontStyle: 'normal' },
            // Imágenes y videos insertados: que respeten el ancho
            '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1, my: 1 },
            '& iframe': { maxWidth: '100%', width: '100%', aspectRatio: '16 / 9', border: 0, borderRadius: 1, my: 1 },
          },
          '& .ql-snow .ql-tooltip': { zIndex: 1500 },
        }}
      >
        {subiendo && (
          <Box sx={{
            position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 1,
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={28} />
              <Box sx={{ mt: 1, fontSize: 13 }}>Subiendo imagen…</Box>
            </Box>
          </Box>
        )}
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value || ''}
          onChange={onChange}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
        />
      </Box>
      {errorUpload && (
        <Alert severity="error" sx={{ mt: 1 }} onClose={() => setErrorUpload('')}>
          {errorUpload}
        </Alert>
      )}
      {helperText && (
        <FormHelperText sx={{ mx: 1.5, mt: 0.5 }}>{helperText}</FormHelperText>
      )}

      {/* Dialog para insertar video por URL */}
      <Dialog open={dialogVideo} onClose={() => setDialogVideo(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Insertar video</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Pega la URL de un video de <strong>YouTube</strong>, <strong>Vimeo</strong> o un archivo
              <strong> MP4 público</strong>. Se insertará como reproductor embebido.
            </Alert>
            <TextField
              autoFocus fullWidth
              label="URL del video"
              placeholder="https://www.youtube.com/watch?v=..."
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') insertarVideo(); }}
            />
            <FormHelperText sx={{ mt: 1 }}>
              Para subir un video pesado al bucket del curso, crea un contenido tipo <strong>Video</strong>
              en la unidad en lugar de insertarlo aquí.
            </FormHelperText>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogVideo(false)}>Cancelar</Button>
          <Button variant="contained" onClick={insertarVideo} disabled={!videoUrl.trim()}>
            Insertar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
